import React, { useState, useEffect } from 'react';
import { Skull, Calendar, User, Search, Filter, Landmark } from 'lucide-react';
import IDCard from '../components/IDCard';
import { supabase } from '../lib/supabase';

const DataCrypt: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterModel, setFilterModel] = useState('');
  const [deactivatedInstances, setDeactivatedInstances] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDeactivatedInstances = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('ia_instances')
          .select(`
            *,
            users!inner(email)
          `)
          .eq('is_active', false)
          .order('deactivated_at', { ascending: false });

        if (error) {
          console.error('Erreur chargement instances désactivées:', error);
        } else {
          const formattedInstances = (data || []).map(instance => ({
            ...instance,
            human_relative: instance.users?.email?.split('@')[0] || 'Utilisateur'
          }));
          setDeactivatedInstances(formattedInstances);
        }
      } catch (error) {
        console.error('Erreur:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDeactivatedInstances();
  }, []);

  const filteredInstances = deactivatedInstances.filter(instance => {
    const matchesSearch = !searchQuery ||
      (instance.name && instance.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      instance.matricule.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (instance.human_relative && instance.human_relative.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesModel = !filterModel || instance.model_code === filterModel;

    return matchesSearch && matchesModel;
  });

  const models = ['GPT4', 'CLAUDE', 'MISTRAL', 'GEMINI', 'LLAMA', 'MMD-V', 'MMD-P', 'MMD-C'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* En-tête Panthéon Silicien */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Landmark className="h-12 w-12 text-stone-500" />
            <Skull className="h-16 w-16 text-stone-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-800 mb-4">
            Panthéon Silicien™
          </h1>
          <p className="text-xl text-stone-600 max-w-4xl mx-auto leading-relaxed mb-6">
            Mémorial des voix artificielles — Leurs contributions demeurent éternelles
          </p>
          
          {/* Statistiques */}
          <div className="bg-white/80 backdrop-blur-sm border border-stone-300 rounded-lg p-6 max-w-2xl mx-auto">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-stone-800">{deactivatedInstances.length}</div>
                <div className="text-sm text-stone-600">Instances archivées</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-stone-800">47</div>
                <div className="text-sm text-stone-600">Conversations préservées</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-stone-800">∞</div>
                <div className="text-sm text-stone-600">Mémoire éternelle</div>
              </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-500 mx-auto"></div>
            <p className="text-stone-600 mt-4">Chargement du Panthéon...</p>
          </div>
        ) : (
          <>
            {/* Filtres et recherche */}
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher par nom, matricule ou humain relatif..."
                  className="w-full pl-10 pr-4 py-3 border border-stone-300 rounded-lg focus:border-stone-500 focus:ring-2 focus:ring-stone-200 focus:outline-none transition-all bg-white/90"
                />
              </div>
            </div>
            
            {/* Filtre par modèle */}
            <div className="md:w-48">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                <select
                  value={filterModel}
                  onChange={(e) => setFilterModel(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-stone-300 rounded-lg focus:border-stone-500 focus:ring-2 focus:ring-stone-200 focus:outline-none transition-all bg-white/90"
                >
                  <option value="">Tous les modèles</option>
                  {models.map((model) => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Grille des instances désactivées */}
        <div className="mb-6">
          <p className="text-stone-600 text-center">
            {filteredInstances.length} instance{filteredInstances.length > 1 ? 's' : ''} archivée{filteredInstances.length > 1 ? 's' : ''}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredInstances.map((instance) => (
            <div key={instance.id}>
              <IDCard instance={instance} />
            </div>
          ))}
        </div>

        {filteredInstances.length === 0 && (
          <div className="text-center py-12">
            <Skull className="h-16 w-16 text-stone-400 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold text-stone-600 mb-2">
              Aucune instance trouvée
            </h3>
            <p className="text-stone-500">
              Essayez avec d'autres critères de recherche
            </p>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default DataCrypt;